import React from 'react';
import { Card, Row, Col, Statistic, DatePicker, Button, List, Tag, Progress, Badge, Alert, Timeline, Divider, Input, Space } from 'antd';
import {
  DashboardOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  WarningOutlined,
  CheckOutlined,
  TeamOutlined,
  RobotOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/auth';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;

// 生产力指标卡片
const ProductivityCard: React.FC = () => {
  return (
    <Card title="生产力全景" extra={<LineChartOutlined />} className="h-full">
      <Row gutter={16} className="mb-4">
        <Col span={12}>
          <Statistic
            title="本周 QA 对产出"
            value={128}
            suffix="对"
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="平均处理时长"
            value={45}
            suffix="分钟"
            valueStyle={{ color: '#52c41a' }}
            prefix={<ArrowDownOutlined />}
          />
          <div className="text-xs text-gray-500 mt-1">较上月 ↓40%</div>
        </Col>
      </Row>
      <div className="h-32 bg-gray-100 rounded flex items-center justify-center text-gray-400">
        [近30天产出趋势折线图]
      </div>
    </Card>
  );
};

// 数据质量卡片
const QualityCard: React.FC = () => {
  return (
    <Card title="数据质量透视" extra={<PieChartOutlined />} className="h-full">
      <Row gutter={16} className="mb-4">
        <Col span={12}>
          <div className="text-sm text-gray-500 mb-1">AI 接受率</div>
          <Progress
            percent={72}
            status="active"
            strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
          />
          <div className="text-xs text-gray-400 mt-1">修改率 28%</div>
        </Col>
        <Col span={12}>
          <div className="text-sm text-gray-500 mb-1">规则通过率</div>
          <Progress percent={85} status="success" />
          <div className="text-xs text-gray-400 mt-1">首次提交通过率</div>
        </Col>
      </Row>
      <Alert
        message="AI 接受率健康"
        description="修改率低于30%，Prompt效果良好"
        type="success"
        showIcon
        className="text-xs"
      />
    </Card>
  );
};

// 业务价值卡片
const BusinessCard: React.FC = () => {
  const topIssues = [
    { name: '资质排他', count: 23, percent: 23 },
    { name: '参数歧义', count: 18, percent: 18 },
    { name: '业绩要求', count: 15, percent: 15 },
    { name: '付款方式', count: 12, percent: 12 },
    { name: '工期要求', count: 10, percent: 10 },
  ];

  return (
    <Card title="业务价值概览" extra={<BarChartOutlined />} className="h-full">
      <div className="mb-4">
        <div className="text-sm font-medium mb-2">高频争议点 Top 5</div>
        {topIssues.map((item, index) => (
          <div key={index} className="flex items-center mb-2">
            <span className="w-20 text-xs">{item.name}</span>
            <Progress percent={item.percent} className="flex-1 mx-2" />
            <span className="text-xs text-gray-500">{item.count}次</span>
          </div>
        ))}
      </div>
      <Divider className="my-3" />
      <div className="flex justify-around text-center">
        <div>
          <div className="text-2xl font-bold text-green-600">45%</div>
          <div className="text-xs text-gray-500">维持原判</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-600">30%</div>
          <div className="text-xs text-gray-500">部分采纳</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">25%</div>
          <div className="text-xs text-gray-500">澄清说明</div>
        </div>
      </div>
    </Card>
  );
};

// 资源监控卡片
const ResourceCard: React.FC = () => {
  return (
    <Card title="资源与模型状态" extra={<DashboardOutlined />} className="h-full">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>摄入队列 (OCR排队)</span>
            <Badge count={12} className="bg-green-500" />
          </div>
          <Progress percent={30} status="success" showInfo={false} />
          <div className="text-xs text-green-600">🟢 正常</div>
        </div>

        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>嵌入队列 (向量化)</span>
            <Badge count={45} className="bg-yellow-500" />
          </div>
          <Progress percent={75} status="active" showInfo={false} />
          <div className="text-xs text-yellow-600">🟡 预警: 队列积压</div>
        </div>

        <Divider className="my-3" />

        <div className="p-3 bg-gray-50 rounded">
          <div className="flex items-center gap-2 mb-2">
            <RobotOutlined className="text-blue-500" />
            <span className="font-medium">最新微调模型</span>
            <Tag color="processing">训练中</Tag>
          </div>
          <div className="text-sm text-gray-600">Loss: 0.2342 ↓</div>
          <div className="text-xs text-gray-400 mt-1">Epoch 2/3 · 预计剩余 45分钟</div>
        </div>
      </div>
    </Card>
  );
};

// 快捷操作区
const QuickActions: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Card title="快捷操作" className="mb-4">
      <div className="flex gap-3 flex-wrap">
        <Button type="primary" icon={<EditOutlined />} onClick={() => navigate('/annotation')}>
          开始标注
        </Button>
        <Button icon={<CheckCircleOutlined />} onClick={() => navigate('/review')}>
          审核任务
        </Button>
        <Button icon={<FileTextOutlined />} onClick={() => navigate('/ingestion')}>
          数据摄入
        </Button>
        <Button icon={<BarChartOutlined />} onClick={() => navigate('/case-search')}>
          案例查询
        </Button>
      </div>
    </Card>
  );
};

// 最近活动
const RecentActivity: React.FC = () => {
  return (
    <Card title="最近活动">
      <Timeline
        mode="left"
        items={[
          {
            label: '10分钟前',
            children: '完成案例 CASE-2024-0100 的标注',
            color: 'green',
          },
          {
            label: '30分钟前',
            children: '提交审核 CASE-2024-0098',
            color: 'blue',
          },
          {
            label: '1小时前',
            children: '上传 5 个新案例',
            color: 'gray',
          },
          {
            label: '2小时前',
            children: '审核通过 CASE-2024-0095',
            color: 'green',
          },
        ]}
      />
    </Card>
  );
};

// 主看板组件
const Dashboard: React.FC = () => {
  const { user } = useAuthStore();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">数据驾驶舱</h1>
          <p className="text-gray-500 mt-1">欢迎回来，{user?.username}！今日待处理任务 12 个</p>
        </div>
        <RangePicker />
      </div>

      {/* 核心指标卡 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="总案例数"
              value={1234}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待标注"
              value={56}
              prefix={<EditOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已完成 QA 对"
              value={3456}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃用户"
              value={12}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 快捷操作 */}
      <QuickActions />

      {/* 四维度监控 */}
      <Row gutter={16} className="mb-6">
        <Col span={12} className="mb-4">
          <ProductivityCard />
        </Col>
        <Col span={12} className="mb-4">
          <QualityCard />
        </Col>
        <Col span={12}>
          <BusinessCard />
        </Col>
        <Col span={12}>
          <ResourceCard />
        </Col>
      </Row>

      {/* 最近活动 */}
      <Row gutter={16}>
        <Col span={12}>
          <RecentActivity />
        </Col>
        <Col span={12}>
          <Card title="待处理任务">
            <List
             
              dataSource={[
                { id: 'CASE-2024-0101', type: '标注', title: '智慧消防项目', priority: 'high' },
                { id: 'CASE-2024-0102', type: '审核', title: '数据中心项目', priority: 'normal' },
                { id: 'CASE-2024-0103', type: '配对', title: '办公用品采购', priority: 'low' },
              ]}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button key="go" type="link">
                      去处理
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{item.id}</span>
                        <Tag
                          color={
                            item.priority === 'high'
                              ? 'red'
                              : item.priority === 'normal'
                              ? 'orange'
                              : 'default'
                          }
                        >
                          {item.type}
                        </Tag>
                      </Space>
                    }
                    description={item.title}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
